import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  Tooltip,
  Tag,
  Row,
  Col,
  Radio,
  Statistic, Divider,
} from "antd";
import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  ProductOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import Layout from "../Layout";

const { Title, Text } = Typography;
const { Option } = Select;
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.21stcenturyav.info";

const LocalProductsPage = () => {
  // State variables
  const [allProducts, setAllProducts] = useState([]); // full list of products
  const [products, setProducts] = useState([]); // filtered list for display
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  /**
   * errorFilter controls which products to show:
   * "hide" (default): hide errors (only non-error products)
   * "all": show all products (errors included)
   * "only": show only error products
   */
  const [errorFilter, setErrorFilter] = useState("hide");
  const [form] = Form.useForm();
  const accessToken = localStorage.getItem("accessToken");

  // Fetch products on component mount and whenever the error filter changes
  useEffect(() => {
    fetchLocalProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorFilter]);

  const fetchLocalProducts = async () => {
    try {
      setLoadingProducts(true);
      const response = await fetch(`${API_BASE_URL}/local_products`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch products.");
      }

      const data = await response.json();
      const fetchedProducts = data.local_products || [];
      // Store the full list for stats
      setAllProducts(fetchedProducts);

      // Filter based on errorFilter mode:
      // "hide": only non-error products; "only": only error products; "all": no filtering.
      let filteredProducts = fetchedProducts;
      if (errorFilter === "hide") {
        filteredProducts = fetchedProducts.filter(
          (product) => product.status !== "error"
        );
      } else if (errorFilter === "only") {
        filteredProducts = fetchedProducts.filter(
          (product) => product.status === "error"
        );
      }
      setProducts(filteredProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
      message.error(error.message || "Error fetching products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  // Compute stats from allProducts
  const totalProducts = allProducts.length;
  const readyCount = allProducts.filter((p) => p.status === "ready").length;
  const validatedCount = allProducts.filter((p) => p.status === "validated").length;
  const sentCount = allProducts.filter((p) => p.status === "sent").length;
  const errorCount = allProducts.filter((p) => p.status === "error").length;

  // Table Columns
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      sorter: (a, b) => a.sku.localeCompare(b.sku),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      sorter: (a, b) => a.category.localeCompare(b.category),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      sorter: (a, b) => a.price - b.price,
      render: (price) => price != null ? `£${Number(price).toFixed(2)}` : "N/A"
      // render: (price) => `£${price.toFixed(2)}`,
    },
    {
      title: "Stock",
      dataIndex: "stock_quantity",
      key: "stock_quantity",
      sorter: (a, b) => a.stock_quantity - b.stock_quantity,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Ready", value: "ready" },
        { text: "Validated", value: "validated" },
        { text: "Sent", value: "sent" },
        { text: "Error", value: "error" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        let color = "default";
        let icon = null;
        let label = status ? status.toUpperCase() : "N/A";

        switch (status) {
          case "ready":
            color = "blue";
            icon = <ProductOutlined style={{ marginRight: 4 }} />;
            label = "READY";
            break;
          case "validated":
            color = "green";
            icon = <CheckCircleOutlined style={{ marginRight: 4 }} />;
            label = "VALIDATED";
            break;
          case "sent":
            color = "cyan";
            icon = <CloudUploadOutlined style={{ marginRight: 4 }} />;
            label = "SENT";
            break;
          case "error":
            color = "red";
            icon = <ExclamationCircleOutlined style={{ marginRight: 4 }} />;
            label = "ERROR";
            break;
          default:
            break;
        }
        return (
          <Tag color={color} style={{ fontSize: 14, padding: "4px 8px" }}>
            {icon}
            {label}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          {record.status === "ready" && (
            <Tooltip title="Validate Product">
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                size="middle"
                onClick={() => validateProduct(record.id)}
              >
                Validate
              </Button>
            </Tooltip>
          )}
          {record.status === "validated" && (
            <Tooltip title="Push Product to WooCommerce">
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                size="middle"
                onClick={() => pushProduct(record.id)}
              >
                Push
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Edit Product">
            <Button
              icon={<EditOutlined />}
              size="middle"
              onClick={() => openEditModal(record)}
            >
              Edit
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete Product"
            description="Are you sure you want to delete this product?"
            onConfirm={() => deleteProduct(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Product">
              <Button type="primary" danger icon={<DeleteOutlined />} size="middle">
                Delete
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Row selection for bulk actions
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
  };

  // Single Product Actions
  const validateProduct = async (productId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/local_products/${productId}/validate`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to validate product.");
      }
      message.success("Product validated successfully.");
      fetchLocalProducts();
    } catch (error) {
      console.error("Error validating product:", error);
      message.error(error.message || "Error validating product.");
    }
  };

  const pushProduct = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/send/${productId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to push product.");
      }
      message.success("Product pushed to WooCommerce successfully.");
      // Allow some time for processing before re-fetching
      setTimeout(() => {
        fetchLocalProducts();
      }, 5000);
    } catch (error) {
      console.error("Error pushing product:", error);
      message.error(error.message || "Error pushing product.");
    }
  };

  const deleteProduct = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/local_products/${productId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete product.");
      }
      message.success("Product deleted successfully.");
      fetchLocalProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
      message.error(error.message || "Error deleting product.");
    }
  };

  // Edit Modal Functions
  const openEditModal = (product) => {
    setEditingProduct(product);
    form.setFieldsValue({
      name: product.name,
      sku: product.sku,
      price: product.price,
      category: product.category,
      stock_quantity: product.stock_quantity,
      brand: product.brand,
      gtin: product.gtin,
      focus_keyword: product.focus_keyword,
      description: product.description,
      short_description: product.short_description,
      tags: product.tags,
    });
    setModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    if (!editingProduct) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/local_products/${editingProduct.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(values),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update product.");
      }
      message.success("Product updated successfully.");
      setModalVisible(false);
      setEditingProduct(null);
      fetchLocalProducts();
    } catch (error) {
      console.error("Error updating product:", error);
      message.error(error.message || "Error updating product.");
    }
  };

  // Bulk Actions
  const bulkValidate = async () => {
    try {
      setLoadingProducts(true);
      await Promise.all(
        selectedRowKeys.map(async (productId) => {
          const response = await fetch(
            `${API_BASE_URL}/local_products/${productId}/validate`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              errorData.detail || `Failed to validate product ID: ${productId}`
            );
          }
        })
      );
      message.success("Selected products validated successfully.");
      setSelectedRowKeys([]);
      fetchLocalProducts();
    } catch (error) {
      console.error("Error validating selected products:", error);
      message.error(error.message || "Error validating selected products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  const bulkPush = async () => {
    try {
      setLoadingProducts(true);
      await Promise.all(
        selectedRowKeys.map(async (productId) => {
          const response = await fetch(`${API_BASE_URL}/send/${productId}`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          });
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              errorData.detail || `Failed to push product ID: ${productId}`
            );
          }
        })
      );
      message.success("Selected products pushed to WooCommerce successfully.");
      setSelectedRowKeys([]);
      fetchLocalProducts();
    } catch (error) {
      console.error("Error pushing selected products:", error);
      message.error(error.message || "Error pushing selected products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  const bulkDelete = async () => {
    try {
      setLoadingProducts(true);
      await Promise.all(
        selectedRowKeys.map(async (productId) => {
          const response = await fetch(`${API_BASE_URL}/local_products/${productId}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          });
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              errorData.detail || `Failed to delete product ID: ${productId}`
            );
          }
        })
      );
      message.success("Selected products deleted successfully.");
      setSelectedRowKeys([]);
      fetchLocalProducts();
    } catch (error) {
      console.error("Error deleting selected products:", error);
      message.error(error.message || "Error deleting selected products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleBulkAction = (action) => {
    let title = "";
    let content = "";
    let onConfirm = null;

    switch (action) {
      case "validate":
        title = "Validate Selected Products";
        content = "Are you sure you want to validate the selected products?";
        onConfirm = bulkValidate;
        break;
      case "push":
        title = "Push Selected Products";
        content =
          "Are you sure you want to push the selected products to WooCommerce?";
        onConfirm = bulkPush;
        break;
      case "delete":
        title = "Delete Selected Products";
        content =
          "Are you sure you want to delete the selected products? This action cannot be undone.";
        onConfirm = bulkDelete;
        break;
      default:
        break;
    }

    if (onConfirm) {
      Modal.confirm({
        title,
        icon: <ExclamationCircleOutlined />,
        content,
        okText: "Yes",
        cancelText: "No",
        onOk: onConfirm,
      });
    }
  };

  return (
    <Layout pageTitle="Manage Local Products">
      <div style={{ padding: "20px", maxWidth: "1600px", margin: "0 auto" }}>
        <Spin
          spinning={loadingProducts}
          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
        >
          {/* Enhanced Statistics Section */}
          <Row justify="center" gutter={[16, 16]} style={{ marginBottom: 20, marginTop: 1 }}>
            <Col xs={24} sm={12} md={3}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 8,
                  textAlign: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <Statistic title="Total" value={totalProducts} />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 8,
                  textAlign: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <Statistic title="Ready" value={readyCount} />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 8,
                  textAlign: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <Statistic title="Validated" value={validatedCount} />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 8,
                  textAlign: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <Statistic title="Sent" value={sentCount} />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 8,
                  textAlign: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <Statistic title="Errors" value={errorCount} />
              </Card>
            </Col>
          </Row>
          <Divider/>

          <Card
            style={{ borderRadius: 6 }}
            title={
              <>
                <Space direction="vertical">
                  <Title level={3} style={{ margin: 0, }}>
                    Manage Local Products
                  </Title>
                  {/* Radio group for error filtering */}
                  <Radio.Group
                    value={errorFilter}
                    onChange={(e) => setErrorFilter(e.target.value)}
                    buttonStyle="solid"
                  >
                    <Radio.Button value="hide">Hide Errors</Radio.Button>
                    <Radio.Button value="all">Show All</Radio.Button>
                    <Radio.Button value="only">Only Errors</Radio.Button>
                  </Radio.Group>
                </Space>
              </>
            }
            bordered={false}
            extra={
              <Space size="middle">
                <Tooltip title="Validate all selected products">
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    size="large"
                    disabled={selectedRowKeys.length === 0}
                    onClick={() => handleBulkAction("validate")}
                  >
                    Validate Selected
                  </Button>
                </Tooltip>
                <Tooltip title="Push all selected products to WooCommerce">
                  <Button
                    type="primary"
                    icon={<CloudUploadOutlined />}
                    size="large"
                    disabled={selectedRowKeys.length === 0}
                    onClick={() => handleBulkAction("push")}
                  >
                    Push Selected
                  </Button>
                </Tooltip>
                <Tooltip title="Delete all selected products">
                  <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    size="large"
                    disabled={selectedRowKeys.length === 0}
                    onClick={() => handleBulkAction("delete")}
                  >
                    Delete Selected
                  </Button>
                </Tooltip>
              </Space>
            }
          >
            <Table
              style={{ marginTop: 16 }}
              rowKey="id"
              columns={columns}
              dataSource={products}
              rowSelection={rowSelection}
              pagination={false} // Display all products without pagination
              scroll={{ x: 1200 }}
            />
          </Card>

          {/* Edit Product Modal */}
          <Modal
            title="Edit Product"
            visible={modalVisible}
            onCancel={() => {
              setModalVisible(false);
              setEditingProduct(null);
              form.resetFields();
            }}
            footer={null}
            destroyOnClose
            bodyStyle={{ paddingTop: 8 }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleEditSubmit}
              initialValues={{
                name: editingProduct?.name || "",
                sku: editingProduct?.sku || "",
                price: editingProduct?.price || 0.0,
                category: editingProduct?.category || "",
                stock_quantity: editingProduct?.stock_quantity || 0,
                brand: editingProduct?.brand || "",
                gtin: editingProduct?.gtin || "",
                focus_keyword: editingProduct?.focus_keyword || "",
                description: editingProduct?.description || "",
                short_description: editingProduct?.short_description || "",
                tags: editingProduct?.tags || "",
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={<Text strong>Product Name</Text>}
                    name="name"
                    rules={[
                      { required: true, message: "Please enter the product name." },
                    ]}
                  >
                    <Input placeholder="Product Name" size="large" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={<Text strong>SKU</Text>}
                    name="sku"
                    rules={[{ required: true, message: "Please enter the SKU." }]}
                  >
                    <Input placeholder="SKU" size="large" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={<Text strong>Category</Text>}
                    name="category"
                    rules={[{ required: true, message: "Please select a category." }]}
                  >
                    <Select placeholder="Select Category" size="large">
                      {[...new Set(products.map((p) => p.category))].map((cat) => (
                        <Option key={cat} value={cat}>
                          {cat}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={<Text strong>Price (£)</Text>}
                    name="price"
                    rules={[{ required: true, message: "Please enter the price." }]}
                  >
                    <InputNumber
                      min={0}
                      step={0.01}
                      style={{ width: "100%" }}
                      formatter={(value) => `£${value}`}
                      parser={(value) => value.replace(/£\s?|(,*)/g, "")}
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={<Text strong>Stock Quantity</Text>}
                    name="stock_quantity"
                    rules={[{ required: true, message: "Please enter the stock quantity." }]}
                  >
                    <InputNumber min={0} style={{ width: "100%" }} size="large" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={<Text strong>Brand</Text>}
                    name="brand"
                    rules={[{ required: true, message: "Please enter the brand." }]}
                  >
                    <Input placeholder="Brand" size="large" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label={<Text strong>GTIN</Text>}
                name="gtin"
                rules={[{ required: true, message: "Please enter the GTIN." }]}
              >
                <Input placeholder="GTIN" size="large" />
              </Form.Item>

              <Form.Item
                label={<Text strong>Focus Keyword</Text>}
                name="focus_keyword"
                rules={[{ required: true, message: "Please enter a focus keyword." }]}
              >
                <Input placeholder="Focus Keyword" size="large" />
              </Form.Item>

              <Form.Item
                label={<Text strong>Description</Text>}
                name="description"
                rules={[{ required: true, message: "Please enter the description." }]}
              >
                <Input.TextArea rows={4} placeholder="Detailed description..." size="large" />
              </Form.Item>

              <Form.Item
                label={<Text strong>Short Description</Text>}
                name="short_description"
                rules={[{ required: true, message: "Please enter a short description." }]}
              >
                <Input.TextArea
                  rows={2}
                  placeholder="Brief description (max 160 chars)..."
                  size="large"
                  maxLength={160}
                  showCount
                />
              </Form.Item>

              <Form.Item
                label={<Text strong>Tags</Text>}
                name="tags"
                rules={[{ required: true, message: "Please enter tags." }]}
                extra="Separate tags with commas"
              >
                <Input placeholder="e.g., gaming, mouse pad, accessories" size="large" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  size="large"
                  block
                  loading={loadingProducts}
                  style={{ marginTop: 8 }}
                >
                  Update Product
                </Button>
              </Form.Item>
            </Form>
          </Modal>
        </Spin>
      </div>
    </Layout>
  );
};

export default LocalProductsPage;
